import { useEffect, useState } from "react";
import { getTigerResource, tigerFormat } from "./tigerResource";

/** One line of the startup step list, as the backend sends it. */
export interface StartupStepEvent {
  /** Identity in the list: a repeat of an id updates that entry in place. */
  id: string;
  /** Resource id of the label's format string. */
  label: string;
  /** `%s` arguments for `label`, in order. */
  args: string[];
  /** false = still running (spinner), true = finished (tick). */
  done: boolean;
}

/** A step ready to render: `text` is localized and formatted. */
export interface StartupStep {
  id: string;
  text: string;
  done: boolean;
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * Localize one backend step. The label is a resource id, so a site lang file
 * override is picked up here; an unknown id falls back to the id itself
 * (getTigerResource), which is visible rather than blank.
 */
export function formatStartupStep(e: StartupStepEvent): StartupStep {
  return {
    id: e.id,
    text: tigerFormat(getTigerResource(e.label), ...(e.args ?? [])),
    done: e.done,
  };
}

/**
 * Merge a step into the list: replace the entry with the same id, keeping its
 * position, or append a new one.
 *
 * Position is kept deliberately — a step that completes must tick in place,
 * not jump to the bottom, or an 18-turbine version check would reorder itself
 * on every reply and be unreadable. Exported for the reducer's own tests.
 */
export function mergeStartupStep(steps: StartupStep[], next: StartupStep): StartupStep[] {
  const at = steps.findIndex((s) => s.id === next.id);
  if (at === -1) return [...steps, next];
  const merged = steps.slice();
  merged[at] = next;
  return merged;
}

/**
 * The startup step list shown inside the "Connecting to Server" dialog:
 * connecting to the ORB, connecting to the tunnel, then one line per turbine
 * as the version check works through them.
 *
 * Why it exists: startup is not quick on a remote site (the tunnel probe is
 * answered by the far side, and the version check talks to every configured
 * turbine in turn — 18 s on tslalarm, minutes on a slow link) and the dialog
 * was a bare spinner with one static sentence, so there was no way to tell
 * progress from a hang. The backend already logged these beats to the `.tlog`;
 * `comms::startup_progress` publishes the same ones as `startup-progress`
 * events.
 *
 * The list is additive and never cleared: it is the record of this startup,
 * and the dialog it lives in is closed (not re-used) once every gate clears.
 * Empty outside Tauri — the web interface has no ORB connect of its own.
 */
export function useStartupProgress(): StartupStep[] {
  const [steps, setSteps] = useState<StartupStep[]>([]);

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    let off: (() => void) | undefined;
    void (async () => {
      try {
        const { listen } = await import("@tauri-apps/api/event");
        const unlisten = await listen<StartupStepEvent>("startup-progress", (ev) => {
          if (cancelled) return;
          setSteps((prev) => mergeStartupStep(prev, formatStartupStep(ev.payload)));
        });
        if (cancelled) {
          unlisten();
          return;
        }
        off = unlisten;
      } catch (e) {
        // A missing progress line must never break startup: the dialog still
        // shows its spinner and its static message without this.
        console.error("startup progress listen failed:", e);
      }
    })();
    return () => {
      cancelled = true;
      off?.();
    };
  }, []);

  return steps;
}
